/**
 * Service registry — the canonical list of every deployed G1 Network service
 * (Cloudflare Workers + Pages + a few containers/externals). This is the source
 * of truth seeded into the shared-DB `api_services` table (see
 * `tools/seed-api-services.ts`) and consumed by the monitoring worker
 * (`g1-analytics/apps/monitoring`) to drive per-service health probes and the
 * per-service status rollup on `admin.g1.network/test-status` +
 * `status.g1.network`.
 *
 * Conventions:
 * - `slug` is the stable identifier (unique). It mirrors the worker name with
 *   the `g1-` prefix stripped for readability (e.g. `g1-shared-api` → `shared-api`).
 *   Slugs are referenced by check tagging in the monitoring worker and by the
 *   CI reporter's repo→service map (`g1-ci-templates/scripts/repo-service-map.json`),
 *   so keep them stable.
 * - `healthEndpoint` is set ONLY for services with a known-working probe. A null
 *   `healthEndpoint` means "registered but not probed" → status stays `unknown`
 *   (honest — not a false `down`). Add a healthEndpoint once a service exposes a
 *   reliable health route.
 * - `metadata.okStatus` lists HTTP statuses that prove the service is alive when
 *   the probe is not a plain 200 (e.g. OIDC-gated `/admin/healthz` → `401`).
 * - `metadata.expectService` (when set) is the value the probe asserts on the
 *   JSON body's `service` field (preserves the identity check the old hardcoded
 *   HEALTH_ENDPOINTS did).
 *
 * Adding a new service: add an entry here, run `pnpm tsx tools/seed-api-services.ts`
 * (or let the deploy CI sync step reconcile it), and `tools/check-worker-inventory.ts`
 * will stop nagging about the unregistered worker name.
 */
export const SERVICE_REGISTRY = [
    // ── Platform core (g1-platform) ───────────────────────────────────────
    {
        slug: "shared-api",
        name: "Shared API",
        description: "Auth, users, sites, templates, global-entities gateway. Sole JWT signer.",
        type: "worker",
        repo: "g1-platform",
        endpoint: "https://api.g1.network",
        healthEndpoint: "/health",
        metadata: { group: "platform", workerName: "g1-shared-api", expectService: "shared-api" },
    },
    {
        slug: "media-api",
        name: "Media API (g1.reviews)",
        description: "Content, ranking, search, AI, news. One worker instance per site.",
        type: "worker",
        repo: "g1-platform",
        endpoint: "https://media-api.g1.network",
        healthEndpoint: "/health",
        metadata: { group: "platform", workerName: "g1-media-api", expectService: "media-api" },
    },
    {
        slug: "console-api",
        name: "Console API",
        description: "Admin console backend (service-binding only).",
        type: "worker",
        repo: "g1-platform",
        metadata: { group: "platform", workerName: "g1-console-api" },
    },
    {
        slug: "deploy-proxy",
        name: "Deploy Proxy",
        description: "OIDC-fronted Cloudflare API proxy for CI deploys.",
        type: "worker",
        repo: "g1-platform",
        endpoint: "https://deploy-proxy.g1.network",
        metadata: { group: "platform", workerName: "g1-deploy-proxy" },
    },
    {
        slug: "structure",
        name: "Platform Structure Map",
        description: "Living, admin-gated architecture map — hosts × workers × datastores, DB access matrix, source-of-truth boundaries.",
        type: "worker",
        repo: "g1-platform",
        endpoint: "https://structure.g1.network",
        healthEndpoint: "/health",
        metadata: { group: "platform", workerName: "g1-structure", expectService: "g1-structure" },
    },
    {
        slug: "leak-scanner",
        name: "Leak Scanner",
        type: "worker",
        repo: "g1-platform",
        metadata: { group: "platform", workerName: "g1-leak-scanner" },
    },
    {
        slug: "games",
        name: "G1 Games",
        description: "Next.js games surface (per-domain cookie auth).",
        type: "worker",
        repo: "g1-platform",
        endpoint: "https://g1.games",
        healthEndpoint: "/health",
        metadata: { group: "platform", workerName: ["g1-game", "g1-games"], expectService: "g1-games" },
    },
    {
        slug: "templates-api",
        name: "Templates API",
        type: "worker",
        repo: "g1-template",
        endpoint: "https://templates.g1.network",
        metadata: { group: "platform", workerName: "g1-templates-api" },
    },
    // ── Auth & identity (g1-auth) ─────────────────────────────────────────
    {
        slug: "auth-oidc",
        name: "Auth (OIDC)",
        description: "OIDC provider — authorize/token/userinfo, JWKS.",
        type: "worker",
        repo: "g1-auth",
        endpoint: "https://auth.g1.network",
        healthEndpoint: "/.well-known/openid-configuration",
        metadata: { group: "auth", workerName: "g1-auth-oidc", okStatus: [200] },
    },
    {
        slug: "auth-api",
        name: "Auth API",
        type: "worker",
        repo: "g1-auth",
        metadata: { group: "auth", workerName: "g1-auth-api" },
    },
    {
        slug: "platform-users-api",
        name: "Platform Users API",
        type: "worker",
        repo: "g1-auth",
        endpoint: "https://users.g1.network",
        metadata: { group: "auth", workerName: "g1-platform-users-api" },
    },
    {
        slug: "platform-users-ro-sync",
        name: "Platform Users RO Sync",
        type: "worker",
        repo: "g1-auth",
        metadata: { group: "auth", workerName: "g1-platform-users-ro-sync" },
    },
    {
        slug: "auth-verify",
        name: "Auth Verify",
        description: "Email/identity verification worker.",
        type: "worker",
        repo: "g1-auth",
        metadata: { group: "auth", workerName: "g1-verify" },
    },
    {
        slug: "contact-vault",
        name: "Contact Vault (PII)",
        description: "PII vault — contacts/import, email storage keyed by user id.",
        type: "worker",
        repo: "g1-pii",
        metadata: { group: "auth", workerName: "g1-contact-vault" },
    },
    // ── Affiliates (g1-affiliates) ────────────────────────────────────────
    {
        slug: "traffic-api",
        name: "Traffic API",
        description: "Affiliate tracking, commissions, payments, brand connections.",
        type: "worker",
        repo: "g1-affiliates",
        endpoint: "https://traffic.g1.network",
        healthEndpoint: "/health",
        metadata: { group: "affiliates", workerName: "g1-traffic-api", expectService: "traffic-api" },
    },
    {
        slug: "go",
        name: "Go Redirect",
        description: "Affiliate /go redirect worker — KV-only, zero DB on critical path.",
        type: "worker",
        repo: "g1-affiliates",
        endpoint: "https://go.g1.network",
        healthEndpoint: "/health",
        metadata: { group: "affiliates", workerName: "g1-go", expectService: "go" },
    },
    {
        slug: "go-proxy",
        name: "Go Proxy",
        type: "worker",
        repo: "g1-affiliates",
        metadata: { group: "affiliates", workerName: "go-proxy" },
    },
    {
        slug: "tg-relay",
        name: "Telegram Relay",
        type: "worker",
        repo: "g1-affiliates",
        metadata: { group: "affiliates", workerName: "g1-tg-relay" },
    },
    // ── Analytics (g1-analytics) ──────────────────────────────────────────
    {
        slug: "analytics-api",
        name: "Analytics API",
        description: "BigQuery + shared Turso analytics dashboards backend.",
        type: "worker",
        repo: "g1-analytics",
        endpoint: "https://analytics-api.g1.network",
        healthEndpoint: "/health",
        metadata: {
            group: "analytics",
            workerName: "g1-analytics-api",
            expectService: "g1-analytics-api",
        },
    },
    {
        slug: "insight-api",
        name: "Insight API",
        description: "Первопартийная поведенческая аналитика и запись сессий для сайтов группы. Заменил Contentsquare: доля выборки, маскирование и стирание — наши, третьей стороны в схеме нет.",
        type: "worker",
        repo: "g1-insight",
        endpoint: "https://insight-api.g1.network",
        healthEndpoint: "/healthz",
        metadata: {
            group: "analytics",
            workerName: "g1-insight-api",
            expectService: "g1-insight-api",
        },
    },
    {
        slug: "analytics-worker",
        name: "Analytics Queue Worker",
        description: "Cloudflare Queue → BigQuery streaming consumer.",
        type: "worker",
        repo: "g1-analytics",
        metadata: { group: "analytics", workerName: "g1-analytics-worker" },
    },
    {
        slug: "ab-tests-api",
        name: "A/B Tests API",
        type: "worker",
        repo: "g1-analytics",
        endpoint: "https://ab-tests.g1.network",
        metadata: { group: "analytics", workerName: "g1-ab-tests-api" },
    },
    {
        slug: "global-signals-api",
        name: "Global Signals API",
        type: "worker",
        repo: "g1-analytics",
        endpoint: "https://global-signals.g1.network",
        metadata: { group: "analytics", workerName: "g1-global-signals-api" },
    },
    {
        slug: "monitoring",
        name: "Monitoring Worker",
        description: "Platform health/test aggregation hub — this service.",
        type: "worker",
        repo: "g1-analytics",
        endpoint: "https://g1-monitoring.rnd-068.workers.dev",
        healthEndpoint: "/health",
        metadata: { group: "analytics", workerName: "g1-monitoring" },
    },
    {
        slug: "reconcile-worker",
        name: "Reconcile Worker",
        type: "worker",
        repo: "g1-analytics",
        metadata: { group: "analytics", workerName: "g1-reconcile-worker" },
    },
    // ── ETL & infra control (g1-etl + standalone ETL workers) ─────────────
    {
        slug: "etl-worker",
        name: "ETL Worker",
        description: "Cron ETL: Affilka + CellXpert + Meta Ads + Keitaro → BigQuery.",
        type: "worker",
        repo: "g1-etl",
        endpoint: "https://etl.g1.network",
        metadata: { group: "etl", workerName: "g1-etl-worker" },
    },
    {
        slug: "cloudflare-api",
        name: "Cloudflare API",
        type: "worker",
        repo: "g1-etl",
        endpoint: "https://cf.g1.network",
        metadata: { group: "etl", workerName: "g1-cloudflare-api" },
    },
    {
        slug: "github-api",
        name: "GitHub API",
        type: "worker",
        repo: "g1-etl",
        endpoint: "https://github-api.g1.network",
        metadata: { group: "etl", workerName: "g1-github-api" },
    },
    {
        slug: "cio-workspaces-api",
        name: "CIO Workspaces API",
        type: "worker",
        repo: "g1-etl",
        endpoint: "https://cio.g1.network",
        metadata: { group: "etl", workerName: "g1-cio-workspaces-api" },
    },
    {
        slug: "etl-cellxpert",
        name: "ETL CellXpert",
        type: "worker",
        repo: "g1-etl-cellxpert",
        metadata: { group: "etl", workerName: "g1-etl-cellxpert" },
    },
    {
        slug: "etl-keitaro",
        name: "ETL Keitaro",
        type: "worker",
        repo: "g1-etl-keitaro",
        metadata: { group: "etl", workerName: "g1-etl-keitaro" },
    },
    {
        slug: "etl-referon",
        name: "ETL Referon",
        type: "worker",
        repo: "g1-etl-referon",
        metadata: { group: "etl", workerName: "g1-etl-referon" },
    },
    {
        slug: "etl-email-sender",
        name: "ETL Email Sender",
        type: "worker",
        repo: "g1-etl-email-sender",
        metadata: { group: "etl", workerName: "g1-etl-email-sender" },
    },
    {
        slug: "etl-template-creator",
        name: "ETL Template Creator",
        type: "worker",
        repo: "g1-etl-template-creator",
        metadata: { group: "etl", workerName: "g1-etl-template-creator" },
    },
    // ── Delivery / email (g1-delivery) ────────────────────────────────────
    {
        slug: "delivery-api",
        name: "Delivery API",
        description: "Email/notification delivery backend.",
        type: "worker",
        repo: "g1-delivery",
        endpoint: "https://delivery-api.g1.network",
        healthEndpoint: "/health",
        metadata: {
            group: "delivery",
            workerName: "g1-delivery-api",
            expectService: "g1-delivery-api",
        },
    },
    {
        slug: "delivery-campaigns-api",
        name: "Delivery Campaigns API",
        type: "worker",
        repo: "g1-delivery",
        endpoint: "https://campaigns.delivery.g1.network",
        metadata: { group: "delivery", workerName: "g1-delivery-campaigns-api" },
    },
    {
        slug: "delivery-providers-api",
        name: "Delivery Providers API",
        type: "worker",
        repo: "g1-delivery",
        endpoint: "https://providers.delivery.g1.network",
        metadata: { group: "delivery", workerName: "g1-delivery-providers-api" },
    },
    {
        slug: "delivery-level-router",
        name: "Delivery Level Router",
        type: "worker",
        repo: "g1-delivery-level-router",
        metadata: { group: "delivery", workerName: "g1-delivery-level-router" },
    },
    {
        slug: "delivery-split-random",
        name: "Delivery Split Random",
        type: "worker",
        repo: "g1-delivery-split-random",
        metadata: { group: "delivery", workerName: "g1-delivery-split-random" },
    },
    {
        slug: "delivery-providers-worker",
        name: "Delivery Providers Worker",
        type: "worker",
        repo: "delivery-providers",
        metadata: { group: "delivery", workerName: "delivery_providers-worker" },
    },
    {
        slug: "delivery-sent-worker",
        name: "Delivery Sent Worker",
        type: "worker",
        repo: "delivery-providers",
        metadata: { group: "delivery", workerName: "sent-worker" },
    },
    {
        slug: "delivery-template-worker",
        name: "Delivery Template Worker",
        type: "worker",
        repo: "delivery-providers",
        metadata: { group: "delivery", workerName: "template-worker" },
    },
    // ── Adtech / RTB (g1-ssp, g1-rtb, g1-dsp, g1-adserver, g1-cmp, g1-creative*) ─
    {
        slug: "ssp-api",
        name: "SSP API",
        type: "worker",
        repo: "g1-ssp",
        endpoint: "https://ssp-api.g1.network",
        healthEndpoint: "/admin/healthz",
        metadata: { group: "rtb", workerName: "g1-ssp-api", okStatus: [200, 401] },
    },
    {
        slug: "rtb-runtime",
        name: "RTB Runtime",
        type: "worker",
        repo: "g1-rtb",
        endpoint: "https://rtb.g1.network",
        healthEndpoint: "/admin/healthz",
        metadata: { group: "rtb", workerName: "g1-rtb-runtime", okStatus: [200, 401] },
    },
    {
        slug: "rtb-bid-cache",
        name: "RTB Bid Cache",
        type: "worker",
        repo: "g1-rtb",
        metadata: { group: "rtb", workerName: "g1-rtb-bid-cache" },
    },
    {
        slug: "dsp-api",
        name: "DSP API",
        type: "worker",
        repo: "g1-dsp",
        endpoint: "https://dsp-api.g1.network",
        healthEndpoint: "/admin/healthz",
        metadata: { group: "rtb", workerName: "g1-dsp-api", okStatus: [200, 401] },
    },
    {
        slug: "adserver-edge",
        name: "Adserver Edge",
        type: "worker",
        repo: "g1-adserver",
        endpoint: "https://adserver.g1.network",
        healthEndpoint: "/edge-admin/healthz",
        metadata: { group: "rtb", workerName: "g1-adserver-edge", okStatus: [200, 401] },
    },
    {
        slug: "adserver-tracking",
        name: "Adserver Tracking",
        type: "worker",
        repo: "g1-adserver",
        metadata: { group: "rtb", workerName: "g1-adserver-tracking" },
    },
    {
        slug: "cmp-api",
        name: "CMP API",
        type: "worker",
        repo: "g1-cmp",
        endpoint: "https://cmp-api.g1.network",
        healthEndpoint: "/admin/healthz",
        metadata: { group: "rtb", workerName: "g1-cmp-api", okStatus: [200, 401] },
    },
    {
        slug: "cmp-iframe",
        name: "CMP Iframe",
        type: "worker",
        repo: "g1-cmp",
        metadata: { group: "rtb", workerName: "g1-cmp-iframe" },
    },
    {
        slug: "creative-scan",
        name: "Creative Scan",
        type: "worker",
        repo: "g1-creative-scan",
        endpoint: "https://creative-scan.g1.network",
        healthEndpoint: "/admin/healthz",
        metadata: { group: "rtb", workerName: "g1-creative-scan", okStatus: [200, 401] },
    },
    {
        slug: "creative-api-proxy",
        name: "Creative API Proxy",
        type: "worker",
        repo: "g1-creative-api-proxy",
        metadata: { group: "rtb", workerName: "g1-creative-api-proxy" },
    },
    {
        slug: "verify-orchestrator",
        name: "Verify Orchestrator",
        type: "worker",
        repo: "g1-verify",
        endpoint: "https://verify.g1.network",
        healthEndpoint: "/admin/healthz",
        metadata: { group: "rtb", workerName: "g1-verify-orchestrator", okStatus: [200, 401] },
    },
    // ── Markets (g1-markets) ──────────────────────────────────────────────
    {
        slug: "markets-api",
        name: "Markets API",
        type: "worker",
        repo: "g1-markets",
        metadata: { group: "markets", workerName: "g1-markets-api" },
    },
    {
        slug: "markets-agent-runtime",
        name: "Markets Agent Runtime",
        type: "worker",
        repo: "g1-markets",
        metadata: { group: "markets", workerName: "g1-markets-agent-runtime" },
    },
    {
        slug: "markets-matching-engine",
        name: "Markets Matching Engine",
        type: "worker",
        repo: "g1-markets",
        metadata: { group: "markets", workerName: "g1-markets-matching-engine" },
    },
    {
        slug: "markets-reconciliation",
        name: "Markets Reconciliation",
        type: "worker",
        repo: "g1-markets",
        metadata: { group: "markets", workerName: "g1-markets-reconciliation" },
    },
    // ── PWA (g1-pwa) ──────────────────────────────────────────────────────
    {
        slug: "pwa-api",
        name: "PWA API",
        type: "worker",
        repo: "g1-pwa",
        metadata: { group: "pwa", workerName: "g1-pwa-api" },
    },
    {
        slug: "pwa-dispatch",
        name: "PWA Dispatch",
        type: "worker",
        repo: "g1-pwa",
        metadata: { group: "pwa", workerName: "g1-pwa-dispatch" },
    },
    {
        slug: "pwa-go",
        name: "PWA Go",
        type: "worker",
        repo: "g1-pwa",
        metadata: { group: "pwa", workerName: "g1-pwa-go" },
    },
    {
        slug: "pwa-postback",
        name: "PWA Postback",
        type: "worker",
        repo: "g1-pwa",
        metadata: { group: "pwa", workerName: "g1-pwa-postback" },
    },
    // ── News (g1-news) ────────────────────────────────────────────────────
    {
        slug: "news-media-api",
        name: "News Media API",
        type: "worker",
        repo: "g1-news",
        endpoint: "https://api.g1.news",
        healthEndpoint: "/health",
        metadata: { group: "news", workerName: "g1-news-media-api" },
    },
    {
        slug: "news-pipeline",
        name: "News Pipeline",
        type: "worker",
        repo: "g1-news",
        metadata: { group: "news", workerName: "g1-news-pipeline" },
    },
    // ── Other platform services ───────────────────────────────────────────
    {
        slug: "global-entities-api",
        name: "Global Entities API (review-api)",
        description: "Canonical entity catalog. Extracted from shared-api 2026-05-07.",
        type: "worker",
        repo: "g1-review",
        endpoint: "https://review-api.g1.network",
        metadata: { group: "platform", workerName: "g1-global-entities-api" },
    },
    {
        slug: "graph-api",
        name: "Graph API",
        description: "Neo4j REST front (cloud graph-direct.g1.network).",
        type: "worker",
        repo: "g1-graph",
        endpoint: "https://graph-api.g1.network",
        healthEndpoint: "/healthz",
        metadata: { group: "platform", workerName: "g1-graph-api", okStatus: [200] },
    },
    {
        slug: "mcp-gateway",
        name: "MCP Gateway",
        description: "mcp.g1.network — tool gateway with OAuth scopes.",
        type: "worker",
        repo: "g1-mcp-gateway",
        endpoint: "https://mcp.g1.network",
        healthEndpoint: "/",
        metadata: { group: "platform", workerName: "g1-mcp-gateway", okStatus: [200] },
    },
    {
        slug: "sms-api",
        name: "SMS API",
        description: "sms-api.g1.network — partner SMS/OTP delivery.",
        type: "worker",
        repo: "g1-sms",
        endpoint: "https://sms-api.g1.network",
        healthEndpoint: "/health",
        metadata: { group: "platform", workerName: "g1-sms-api", expectService: "sms-api" },
    },
    {
        slug: "mediaplan",
        name: "Media Plan",
        description: "mediaplan.g1.network — media-plan Gantt with partner slot booking (one Worker: API + SPA).",
        type: "worker",
        repo: "g1-mediaplan",
        endpoint: "https://mediaplan.g1.network",
        healthEndpoint: "/healthz",
        metadata: { group: "platform", workerName: "g1-mediaplan", expectService: "g1-mediaplan" },
    },
    {
        slug: "minisites",
        name: "Minisites",
        description: "minisites.g1.network — minisite constructor: admin API + SPA (one Worker). Launches offer-list sites on their own domains.",
        type: "worker",
        repo: "g1-minisites",
        endpoint: "https://minisites.g1.network",
        healthEndpoint: "/healthz",
        metadata: { group: "platform", workerName: "g1-minisites", expectService: "g1-minisites" },
    },
    {
        slug: "minisites-site",
        name: "Minisites — serving Worker",
        description: "g1-minisites-site on the dedicated minisites Cloudflare account — renders every minisite domain by Host from a KV payload (no fixed host; zone routes bound per site at launch).",
        type: "worker",
        repo: "g1-minisites",
        metadata: { group: "platform", workerName: "g1-minisites-site", account: "minisites" },
    },
    {
        slug: "tasks-api",
        name: "Tasks API",
        description: "tasks.g1.network backend.",
        type: "worker",
        repo: "g1-tasks",
        endpoint: "https://tasks-api.g1.network",
        metadata: { group: "platform", workerName: "g1-tasks-api" },
    },
    {
        slug: "emailnetwork",
        name: "Email Network",
        description: "emailnetwork.g1.network — email-forensics / spam-investigation console.",
        type: "worker",
        repo: "g1-emailnetwork",
        endpoint: "https://emailnetwork.g1.network",
        healthEndpoint: "/healthz",
        metadata: {
            group: "platform",
            workerName: "g1-emailnetwork",
            expectService: "g1-emailnetwork",
        },
    },
    {
        slug: "reports",
        name: "Reports",
        description: "reports.g1.network — staff report builder over the domains (BigQuery analytics_mart + own D1).",
        type: "worker",
        repo: "g1-reports",
        endpoint: "https://reports.g1.network",
        healthEndpoint: "/healthz",
        metadata: {
            group: "platform",
            workerName: "g1-reports",
            expectService: "g1-reports",
        },
    },
    {
        slug: "cryptonetwork",
        name: "Crypto Network",
        description: "cryptonetwork.g1.network — crypto-wallet / transaction forensics console (partner payout monitoring + overlap graph).",
        type: "worker",
        repo: "g1-cryptonetwork",
        endpoint: "https://cryptonetwork.g1.network",
        healthEndpoint: "/healthz",
        metadata: {
            group: "platform",
            workerName: "g1-cryptonetwork",
            expectService: "g1-cryptonetwork",
        },
    },
    {
        slug: "search-api",
        name: "Search API",
        type: "worker",
        repo: "g1-search",
        endpoint: "https://search-api.g1.network",
        metadata: { group: "platform", workerName: "g1-search-api" },
    },
    {
        slug: "search-indexer",
        name: "Search Indexer",
        type: "worker",
        repo: "g1-search",
        metadata: { group: "platform", workerName: "g1-search-indexer" },
    },
    {
        slug: "push-api",
        name: "Push API",
        type: "worker",
        repo: "g1-push",
        metadata: { group: "platform", workerName: "g1-push-api" },
    },
    {
        slug: "crm-api",
        name: "CRM API",
        type: "worker",
        repo: "g1-crm",
        metadata: { group: "platform", workerName: "g1-crm-api" },
    },
    {
        slug: "customer-relationship",
        name: "Customer Relationship",
        type: "worker",
        repo: "g1-customer-relationship",
        metadata: { group: "platform", workerName: "g1-customer-relationship" },
    },
    {
        slug: "webhooks-api",
        name: "Webhooks API",
        type: "worker",
        repo: "g1-webhooks",
        metadata: { group: "platform", workerName: "g1-webhooks-api" },
    },
    {
        slug: "cdn-worker",
        name: "CDN Worker",
        type: "worker",
        repo: "g1-cdn",
        metadata: { group: "platform", workerName: "g1-cdn-worker" },
    },
    {
        slug: "cdn-proxy",
        name: "CDN Proxy",
        description: "Per-customer cdn.<domain> upstream proxy. Bound dynamically by sites_provision_frontend's cf-cdn-domain step.",
        type: "worker",
        repo: "g1-cdn",
        endpoint: "https://cdn.g1.network",
        healthEndpoint: "/healthz",
        metadata: { group: "platform", workerName: "g1-cdn-proxy" },
    },
    {
        slug: "oss-worker",
        name: "OSS Worker",
        type: "worker",
        repo: "g1-oss",
        metadata: { group: "platform", workerName: "g1-oss-worker" },
    },
    {
        slug: "manager-agent-api",
        name: "Manager Agent API",
        description: "agent.g1.group governance agent runtime.",
        type: "worker",
        repo: "g1-manager-agent",
        endpoint: "https://agent.g1.group",
        metadata: { group: "platform", workerName: "g1-manager-agent-api" },
    },
    {
        slug: "status-worker",
        name: "Status Worker (adtech)",
        description: "status.g1.network adtech status page (separate system).",
        type: "worker",
        repo: "g1-status",
        endpoint: "https://status.g1.network",
        metadata: { group: "platform", workerName: "g1-status-worker" },
    },
    // ── Per-site media-api instances (g1-landing fan-out) ─────────────────
    // Registered for inventory completeness; healthEndpoint left null until a
    // site is confirmed live to avoid false `down` on un-provisioned domains.
    {
        slug: "spielgeil-media-api",
        name: "Media API (spielgeil.de)",
        type: "worker",
        repo: "g1-landing",
        endpoint: "https://api.spielgeil.de",
        healthEndpoint: "/health",
        metadata: { group: "media-sites", workerName: "spielgeil-media-api" },
    },
    {
        slug: "saycasinoname-media-api",
        name: "Media API (saycasinoname.com)",
        type: "worker",
        repo: "g1-landing",
        endpoint: "https://api.saycasinoname.com",
        healthEndpoint: "/health",
        metadata: { group: "media-sites", workerName: "saycasinoname-media-api" },
    },
    {
        slug: "spielgeil-com-media-api",
        name: "Media API (spielgeil.com)",
        type: "worker",
        repo: "g1-landing",
        metadata: { group: "media-sites", workerName: "spielgeil-com-media-api" },
    },
    {
        slug: "geilespiele-com-media-api",
        name: "Media API (geilespiele.com)",
        type: "worker",
        repo: "g1-landing",
        metadata: { group: "media-sites", workerName: "geilespiele-com-media-api" },
    },
    {
        slug: "rateofcasino-com-media-api",
        name: "Media API (rateofcasino.com)",
        type: "worker",
        repo: "g1-landing",
        metadata: { group: "media-sites", workerName: "rateofcasino-com-media-api" },
    },
    {
        slug: "blue-vanilla-media-api",
        name: "Media API (blue-vanilla.de)",
        type: "worker",
        repo: "g1-landing",
        metadata: { group: "media-sites", workerName: "blue-vanilla-media-api" },
    },
    {
        slug: "casinotest-eu-media-api",
        name: "Media API (casinotest.eu)",
        type: "worker",
        repo: "g1-landing",
        metadata: { group: "media-sites", workerName: "casinotest-eu-media-api" },
    },
    {
        slug: "flame-play-media-api",
        name: "Media API (flame-play)",
        type: "worker",
        repo: "g1-landing",
        metadata: { group: "media-sites", workerName: "flame-play-media-api" },
    },
    {
        slug: "spielstop-de-media-api",
        name: "Media API (spielstop.de)",
        type: "worker",
        repo: "g1-landing",
        metadata: { group: "media-sites", workerName: "spielstop-de-media-api" },
    },
    {
        slug: "youinsider-net-media-api",
        name: "Media API (youinsider.net)",
        type: "worker",
        repo: "g1-landing",
        metadata: { group: "media-sites", workerName: "youinsider-net-media-api" },
    },
    {
        slug: "landing-proxy",
        name: "Landing Proxy",
        description: "Per-site media-api hostname proxy (api.<domain> routing).",
        type: "worker",
        repo: "g1-landing",
        metadata: { group: "media-sites", workerName: "g1-landing-proxy" },
    },
];
/** Lookup by slug. */
export const SERVICE_BY_SLUG = Object.fromEntries(SERVICE_REGISTRY.map((s) => [s.slug, s]));
/** Slugs that have a configured health probe. */
export const PROBED_SERVICE_SLUGS = SERVICE_REGISTRY.filter((s) => s.healthEndpoint && s.endpoint).map((s) => s.slug);
//# sourceMappingURL=service-registry.js.map